/**
 * Enquetes da viagem (em Mais): tudo o que está aberto, depois o que já
 * fechou. Criar aqui pede o dia — no Roteiro, o dia é o da página.
 */
import { featureEnabled, resolveToday } from '@nv/core';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAgora } from '../../demo/DemoContext';
import { useDiasDaViagem } from '../../trip/dias';
import { useTrip } from '../../trip/TripContext';
import { tripInputDaViagem } from '../../trip/tripInput';
import { Icon } from '../../ui/icons';
import { EmptyState, ErrorState, ScreenHeader, SectionLabel, Skeleton } from '../../ui/primitives';
import { useEnquetes } from './api';
import { EnqueteCard } from './EnqueteCard';
import { NovaEnquete } from './NovaEnquete';

export function EnquetesScreen() {
  const { trip, tripId, members } = useTrip();
  const ligado = featureEnabled(trip.features, 'enquetes');
  const viagem = tripInputDaViagem(trip, members.length);
  const agora = useAgora(viagem);
  const dias = useDiasDaViagem(tripId);
  const enquetes = useEnquetes(tripId, undefined, ligado);
  // `/enquetes?novo=1` chega do "+" da barra já criando (lote de aparência 1).
  const [busca] = useSearchParams();
  const [criando, setCriando] = useState(busca.get('novo') === '1');

  if (!ligado) {
    return (
      <section className="screen">
        <EmptyState ilustracao="enquetes" title="Enquetes desligadas" description="Quem administra a viagem desligou esta funcionalidade." />
      </section>
    );
  }

  const hoje = (() => {
    try {
      return viagem ? resolveToday(viagem.timeZone, agora, viagem.utcOffsetSchedule) : null;
    } catch {
      return null;
    }
  })();
  const lista = dias.days ?? [];
  // Por padrão, a enquete é de hoje; fora do período, do primeiro dia que ainda vem (ou o último).
  const diaPadrao = lista.find((d) => d.date === hoje) ?? lista.find((d) => hoje !== null && d.date > hoje) ?? lista[lista.length - 1];
  const abertas = (enquetes.data?.polls ?? []).filter((e) => e.isOpen);
  const fechadas = (enquetes.data?.polls ?? []).filter((e) => !e.isOpen);

  return (
    <section className="screen enq__tela">
      <ScreenHeader
        title="Enquetes"
        trailing={
          diaPadrao && (abertas.length > 0 || fechadas.length > 0) ? (
            <button type="button" className="nv-btn nv-btn--primary nv-btn--pilula enq__nova" data-testid="nova-enquete" onClick={() => setCriando(true)}>
              <Icon name="criar" size={18} /> Nova enquete
            </button>
          ) : undefined
        }
      />

      {enquetes.isLoading && <Skeleton height={220} raio="hero" count={2} />}
      {enquetes.isError && <ErrorState title="Não foi possível carregar" description="Verifique a conexão e tente de novo." onRetry={() => void enquetes.refetch()} />}

      {enquetes.isSuccess && abertas.length === 0 && fechadas.length === 0 && (
        <EmptyState
          ilustracao="enquetes"
          title="Nenhuma enquete ainda"
          description={'"Onde vamos jantar hoje?" — pergunte ao grupo. A vencedora vira atividade do roteiro.'}
          {...(diaPadrao ? { action: { label: 'Nova enquete', onClick: () => setCriando(true) } } : {})}
        />
      )}

      {abertas.length > 0 && (
        <>
          <SectionLabel>ABERTAS</SectionLabel>
          {abertas.map((e) => (
            <EnqueteCard key={e.id} enquete={e} mostrarDia />
          ))}
        </>
      )}
      {fechadas.length > 0 && (
        <>
          <SectionLabel>ENCERRADAS</SectionLabel>
          {fechadas.map((e) => (
            <EnqueteCard key={e.id} enquete={e} mostrarDia />
          ))}
        </>
      )}

      {criando && diaPadrao && <NovaEnquete dia={diaPadrao} dias={lista} onFechar={() => setCriando(false)} />}
    </section>
  );
}
